import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Search, ExternalLink, MessageSquare, Brain, Sparkles, Image, Palette, Aperture, Code, FileText, Mic, Video, Globe as GlobeIcon, Layout, PenTool, Film, Smile } from 'lucide-react';
import SEO from '../components/SEO';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  'message-square': MessageSquare,
  'brain': Brain,
  'sparkles': Sparkles,
  'image': Image,
  'palette': Palette,
  'aperture': Aperture,
  'code': Code,
  'file-text': FileText,
  'mic': Mic,
  'video': Video,
  'search': GlobeIcon,
  'layout': Layout,
  'pen-tool': PenTool,
  'film': Film,
  'smile': Smile,
};

const categoryColors = {
  chatbot: 'bg-blue-50 text-blue-700 border-blue-200',
  image: 'bg-orange-50 text-orange-700 border-orange-200',
  code: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  productivity: 'bg-amber-50 text-amber-700 border-amber-200',
  audio: 'bg-rose-50 text-rose-700 border-rose-200',
  video: 'bg-sky-50 text-sky-700 border-sky-200',
  search: 'bg-teal-50 text-teal-700 border-teal-200',
  design: 'bg-pink-50 text-pink-700 border-pink-200',
  writing: 'bg-orange-50 text-orange-700 border-orange-200',
  platform: 'bg-slate-100 text-slate-700 border-slate-300',
};

const categoryLabels = {
  fr: { chatbot: 'Chatbots', image: 'Images', code: 'Code', productivity: 'Productivite', audio: 'Audio', video: 'Video', search: 'Recherche', design: 'Design', writing: 'Redaction', platform: 'Plateforme' },
  en: { chatbot: 'Chatbots', image: 'Images', code: 'Code', productivity: 'Productivity', audio: 'Audio', video: 'Video', search: 'Search', design: 'Design', writing: 'Writing', platform: 'Platform' },
};

export default function LexiconPage() {
  const { language, t } = useLanguage();
  const { user, getAuthHeaders } = useAuth();
  const [entries, setEntries] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  // Track lexicon visit for badge
  useEffect(() => {
    if (user) {
      axios.post(`${API}/badges/track-action`, { action: 'lexicon_visit' }, { headers: getAuthHeaders(), withCredentials: true }).catch(() => {});
    }
  }, [user, getAuthHeaders]);

  useEffect(() => {
    const fetchLexicon = async () => {
      try {
        const params = {};
        if (search) params.search = search;
        if (selectedCategory !== 'all') params.category = selectedCategory;
        const res = await axios.get(`${API}/lexicon`, { params });
        setEntries(res.data);
      } catch (err) {
        console.error('Lexicon fetch error:', err);
      }
      setLoading(false);
    };
    const timer = setTimeout(fetchLexicon, 300);
    return () => clearTimeout(timer);
  }, [search, selectedCategory]);

  const categories = ['all', 'chatbot', 'image', 'code', 'productivity', 'audio', 'video', 'search', 'design', 'writing', 'platform'];

  return (
    <div className="min-h-screen bg-[#E8ECF1]" data-testid="lexicon-page">
      <SEO
        title={language === 'fr' ? 'Lexique IA - 15+ Outils' : 'AI Lexicon - 15+ Tools'}
        description={language === 'fr'
          ? "Explorez notre lexique complet de 15+ outils IA : ChatGPT, Claude, Midjourney, Copilot, DALL-E et plus. Définitions, cas d'usage et exemples concrets."
          : "Explore our comprehensive lexicon of 15+ AI tools: ChatGPT, Claude, Midjourney, Copilot, DALL-E and more. Definitions, use cases and real examples."}
        path="/lexicon"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <img src="https://customer-assets.emergentagent.com/job_2071b760-1de1-4ab9-81d8-8ca2ec59ffcc/artifacts/lq8u7cy5_LOGO%20IA.png" alt="IA Academy" className="w-10 h-10 rounded-lg" data-testid="lexicon-logo" />
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Poppins' }} data-testid="lexicon-title">
              {t('lexicon_title')}
            </h1>
          </div>
          <p className="text-slate-500 mt-2 text-base md:text-lg">{t('lexicon_subtitle')}</p>
        </div>

        {/* Search & Filters */}
        <div className="mb-8 space-y-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder={t('lexicon_search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-11 bg-white"
              data-testid="lexicon-search-input"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <Button
                key={cat}
                variant={selectedCategory === cat ? 'default' : 'outline'}
                size="sm"
                className={`text-xs ${
                  selectedCategory === cat
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
                onClick={() => setSelectedCategory(cat)}
                data-testid={`lexicon-filter-${cat}`}
              >
                {cat === 'all' ? t('lexicon_all') : (categoryLabels[language]?.[cat] || cat)}
              </Button>
            ))}
          </div>
        </div>

        {/* Entries Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {entries.map((entry) => {
              const IconComponent = iconMap[entry.icon] || Sparkles;
              return (
                <div
                  key={entry.entry_id}
                  className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-shadow"
                  data-testid={`lexicon-entry-${entry.entry_id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${categoryColors[entry.category] || 'bg-slate-100 text-slate-600'}`}>
                        <IconComponent className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Poppins' }}>{entry.name_display}</h3>
                        <Badge variant="outline" className={`text-[10px] mt-0.5 ${categoryColors[entry.category] || ''}`}>
                          {categoryLabels[language]?.[entry.category] || entry.category}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-slate-500 leading-relaxed mb-4">
                    {language === 'fr' ? entry.description_fr : entry.description_en}
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4">
                    <p className="text-xs text-slate-400 font-medium uppercase tracking-wide mb-1">{t('lexicon_example')}</p>
                    <p className="text-sm text-slate-600 italic">
                      {language === 'fr' ? entry.example_fr : entry.example_en}
                    </p>
                  </div>
                  {entry.website && (
                    <a
                      href={entry.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium"
                      data-testid={`lexicon-link-${entry.entry_id}`}
                    >
                      <ExternalLink className="w-3.5 h-3.5" /> {t('lexicon_visit')}
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!loading && entries.length === 0 && (
          <div className="text-center py-12">
            <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">{language === 'fr' ? 'Aucun resultat trouve' : 'No results found'}</p>
          </div>
        )}
      </div>
    </div>
  );
}
